import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { ellipsis } from '@chancenwerk/client/util.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-date-picker.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-tabs.js';
import {
  subscribeWorkshopAttendance,
  WorkshopAttendanceSearchResult,
} from '@chancenwerk/shared/workshops/api/subscribeWorkshopAttendance.js';
import { Workshop } from '@chancenwerk/shared/workshops/entities/Workshop.js';
import { WorkshopAppointment } from '@chancenwerk/shared/workshops/entities/WorkshopAppointment.js';
import { WorkshopFilter } from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { switchMap, tap } from 'rxjs';
import '../components/akademie-component-workshop-edit.js';
import '../components/akademie-component-workshop-entry.js';
import '../components/akademie-component-workshop-migration-onboarding.js';
import '../components/akademie-component-workshop-migration.js';
import '../components/akademie-component-workshop-rag-by-state.js';
import '../components/akademie-component-workshop-rag-by-workshop.js';
import '../components/akademie-component-workshop-reporting.js';
import { AkademieDialogWorkshopAppointmentEdit } from '../components/dialogs/akademie-dialog-workshop-appointment-edit.js';

/**
 * @customElement akademie-page-workshop
 */
@customElement('akademie-page-workshop')
export class RaumPageWorkshop extends CrPage {
  @property() workshopID = new RXController(this, '');

  @state() private readonly _workshop = new RXController(
    this,
    this.workshopID.observable.pipe(
      switchMap(id => Workshop.subscribeByID<Workshop>(id ?? '')(Workshop.allFields)),
      tap(workshop => {
        this.label = ellipsis(workshop?.title ?? 'Workshop', 60);
        this.description = workshop?.requiresTraining ? 'TtT-Schulung' : 'Optionaler Workshop';
        this._workshopFilter.value.workshopID = workshop?._id ?? '';
        this._workshopFilter.next(this._workshopFilter.value);
      }),
    ),
  );
  @state() private _tab: 'Übersicht' | 'Statistik' | 'Termine' = 'Übersicht';
  @state() private readonly _workshopFilter = new RXController(this, new WorkshopFilter({ workshopID: '' }));

  constructor() {
    super();

    this.label = 'Workshop';
    this.description = '';
    this.permission = ChancenPermission.akademie.EDIT_WORKSHOP;
    this.backRoute = '/';
  }

  override content() {
    return html`${this._getTabs()}
      <cr-flex padding="sm">
        ${{
          Übersicht: () => this._overview(),
          Statistik: () => this._statistics(),
          Termine: () => this._appointments(),
        }[this._tab]()}
      </cr-flex>`;
  }

  private _getTabs() {
    return html`
      <cr-tabs .selected=${['Übersicht', 'Statistik', 'Termine'].indexOf(this._tab)}>
        <cr-tab @click=${() => (this._tab = 'Übersicht')}> Übersicht </cr-tab>
        <cr-tab @click=${() => (this._tab = 'Statistik')}> Statistik </cr-tab>
        <cr-tab @click=${() => (this._tab = 'Termine')}> Termine </cr-tab>
      </cr-tabs>
    `;
  }

  private _overview() {
    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>Informationen</chancen-component-card-title>
      <akademie-component-workshop-edit .workshop=${this._workshop.value}></akademie-component-workshop-edit>
    </cr-card>`;
  }

  private _appointments() {
    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Termine</chancen-component-card-title>
      <cr-data-view
        .options=${{
          class: WorkshopAppointment,
          view: new View<typeof WorkshopAppointment>({
            filter: new AdornisFilter({
              sortingFields: ['start'],
              aspects: [new StringAspect({ mode: 'equals', fieldName: 'workshopID', value: this.workshopID.value })],
            }),
          }),

          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: '',
              columnWidth: 'max-content',
              customID: 'icon',
              getFieldMapping: w => html`
                <cr-button mode="icon" icon="visibility" href=${`/appointment/${w._id}`}></cr-button>
              `,
            },
            {
              columnName: 'Wann',
              customID: 'start',
              selectionSet: { start: 1 },
              getFieldMapping: w => w.start?.toFormat('dd.MM.yyyy HH:mm') ?? 'nicht eingetragen',
            },
            {
              columnName: 'Teilnehmer',
              customID: 'attendance',
              selectionSet: { attendance: { _id: 1 } },
              getFieldMapping: w => w.attendance?.length ?? 0,
            },
          ],
        } as DataViewOptions<typeof WorkshopAppointment>}
      >
      </cr-data-view>
      <cr-button
        icon="add"
        ${css({ maxWidth: '250px' })}
        @click=${async () => {
          await AkademieDialogWorkshopAppointmentEdit.showPopup({ props: { workshopID: this.workshopID.value } });
        }}
        >Erstellen</cr-button
      >
    </cr-card>`;
  }

  private _statistics() {
    return html` <cr-card space="md" padding="md">
      ${this._workshop.value?.requiresTraining
        ? html`<akademie-component-workshop-reporting
            .workshopID=${this._workshop.value._id}
          ></akademie-component-workshop-reporting>`
        : html`${this._detailsOptionalFilter()} ${this._detailsOptional()}`}
    </cr-card>`;
  }

  private _detailsOptionalFilter() {
    return html`<cr-text bold>Filter</cr-text>
      <cr-grid columns="0.7fr 1fr" space="md">
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          clearable
          placeholder="Von"
          click-to-save
          flex
          .value=${this._workshopFilter.value.from}
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._workshopFilter.value.from = e.detail.value;
            if (e.detail.value) {
              if (!this._workshopFilter.value.till || this._workshopFilter.value.till < e.detail.value) {
                this._workshopFilter.value.till = e.detail.value.endOf('month');
              }
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          placeholder="Bis"
          clearable
          click-to-save
          flex
          .value=${this._workshopFilter.value.till}
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._workshopFilter.value.till = e.detail.value;
            if (e.detail.value) {
              if (!this._workshopFilter.value.from || this._workshopFilter.value.from > e.detail.value) {
                this._workshopFilter.value.from = e.detail.value.startOf('month');
              }
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
      </cr-grid>`;
  }

  private _detailsOptional() {
    return html`<cr-text bold>Einträge</cr-text>
      <cr-data-view
        .options=${{
          class: WorkshopAttendanceSearchResult,
          operation: subscribeWorkshopAttendance,
          view: new View<typeof WorkshopAttendanceSearchResult>({ filter: this._workshopFilter.value }),
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: '',
              columnWidth: 'max-content',
              customID: 'icon',
              selectionSet: { user: { vorname: 1, nachname: 1 } },
              getFieldMapping: w => html`
                <cr-button
                  mode="icon"
                  icon="person"
                  href="${`${RaumRoutes.editEmployee}/${w.user?._id}`}"
                  target="_blank"
                ></cr-button>
              `,
            },
            {
              columnName: 'Name',
              columnWidth: 'max-content',
              customID: 'username',
              selectionSet: { user: { vorname: 1, nachname: 1, email: 1 } },
              getFieldMapping: w => w.user?.toString(),
            },
            {
              columnName: 'Durchführungsdatum',
              columnWidth: 'max-content',
              customID: 'date',
              selectionSet: { workshopID: 1, date: 1 },
              getFieldMapping: w => {
                return w.date?.toLocaleString() ?? 'nicht eingetragen';
              },
            },
            {
              columnName: 'Status',
              columnWidth: 'max-content',
              customID: 'state',
              selectionSet: { state: 1 },
              getFieldMapping: w => {
                return w.state ?? 'nicht eingetragen';
              },
            },
          ],
        } as DataViewOptions<typeof WorkshopAttendanceSearchResult>}
      >
      </cr-data-view>`;
  }
}
